
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Button,
  Paper,
  Snackbar,
  Alert,
  styled,
  Container,
  Grid,
  CircularProgress,
  InputAdornment,
  AlertTitle,
  Box
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  useGridApiContext,
} from '@mui/x-data-grid';
import axios from 'axios';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';

// Styled Components
const FormContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  minHeight: '100vh',
  padding: theme.spacing(4),
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.shape.borderRadius * 2,
  width: '100%',
  maxWidth: '800px',
  background: 'linear-gradient(to-bottom, #ffffff, #f8f9fa)',
}));

const FormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  fontWeight: 600,
  color: '#000000',
  textAlign: 'left',
  textTransform: 'uppercase',
  marginTop: '-50px',
  fontSize: '16px',
}));

const FormTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused fieldset': {
      borderWidth: '1px',
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontSize: '13px',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main,
  },
  '& .MuiInputBase-input': {
    fontSize: '13px',
    padding: '10px 14px',
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'none',
  fontSize: '0.875rem',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .MuiDataGrid-cell:hover': {
    cursor: 'pointer',
  },
  '& .even': {
    backgroundColor: '#F8F8F8',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '& .odd': {
    backgroundColor: '#E6F0FF',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#31478F',
    color: 'white',
    fontSize: '12px',
  },
  '& .MuiDataGrid-cell': {
    fontSize: '12px',
  },
  '& .MuiDataGrid-iconButton': {
    fontSize: '12px',
    color: 'grey',
  },
}));

const CustomToolbar = () => {
  const apiRef = useGridApiContext();

  const handleReset = () => {
    apiRef.current.setFilterModel({ items: [] });
    apiRef.current.setSortModel([]);
    apiRef.current.setColumnVisibilityModel({});
    apiRef.current.setDensity('standard');
  };

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <Button
        size="small"
        onClick={handleReset}
        startIcon={<RefreshIcon fontSize="medium" />}
        sx={{
          fontSize: '14px',
          textTransform: 'uppercase',
          padding: '4px 8px',
          minWidth: 'auto',
          '& .MuiButton-startIcon': {
            marginRight: '4px',
            '& > *:nth-of-type(1)': {
              fontSize: '20px',
            },
          },
        }}
      >
        Reset
      </Button>
    </GridToolbarContainer>
  );
};

const ManageUsers = ({ isOnboard = false, client }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get('status');
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    userName: '',
    password: '',
    role: 'Employee',
    email: '',
    userStatus: 'Active',
    accountId: isOnboard ? client : '100',
  });
  const [userNameError, setUserNameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [accountError, setAccountError] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [isCopiedToClipboard, setIsCopiedToClipboard] = useState(false);
  const [isEmailAvailable, setIsEmailAvailable] = useState(true);
  const [isUserCreated, setIsUserCreated] = useState(false);
  const [isUserUpdated, setIsUserUpdated] = useState(false);
  const [isAddUserSelected, setAddUserSelected] = useState(isOnboard);
  const [isUpdateUserSelected, setUpdateUserSelected] = useState(false);
  const [users, setUsers] = useState([]);
  const [accountDetails, setAccountDetails] = useState([]);
    const [userListView, setUserListView] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const loggedInUserClientId = localStorage.getItem('clientId');
  const loggedInUserName = localStorage.getItem('userName');
  const loggedInRole = localStorage.getItem('role');
  const doNotPerformUserFiltration = loggedInRole === 'Super Admin';
  const hasEmployees = users.some(user => user.role === 'Employee');

  const userStatuses = [
    { id: 'Active', value: 'Active' },
    { id: 'Inactive', value: 'Inactive' },
  ];

  const userRoles = [
    { id: 4, value: 'Employee' },
  ];

  const NoRecordsFoundRow = [
    {
      id: '',
      userName: '',
      account: 'No records found',
      userStatus: '',
      role: '',
    },
  ];

  useEffect(() => {
    if (!localStorage.getItem('isLoggedIn')) {
      setLoggedIn(false);
      navigate('/');
    } else {
      setLoggedIn(true);
      setLoading(true);
      Promise.all([getUsers(), getClients()]).then(() => {
        generateListViewDetails();
        setLoading(false);
      });
    }
  }, [navigate]);

  const getUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/v1/user`);
      const data = response.data;
      const filteredData = doNotPerformUserFiltration
        ? data.filter(user => ['Admin', 'User', 'Employee', 'Super Admin'].includes(user?.role))
        : data.filter(user => user?.clientID === loggedInUserClientId);
      setUsers(filteredData);
      return filteredData;
    } catch (error) {
      setSnackbarMessage('Failed to fetch users.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return [];
    }
  };

  const getClients = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/v1/client`);
      const data = response.data;
      const filteredAccount = data.filter(
        account => account.clientID === '100' && account.customerID === 'DGCQ1' && account.clientName === 'KSG Fuel'
      );
      setAccountDetails(filteredAccount);
      return filteredAccount;
    } catch (error) {
      setSnackbarMessage('Failed to fetch clients.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return [];
    }
  };

  const generateListViewDetails = async () => {
    const users = await getUsers();
    const accounts = await getClients();
    if (users.length > 0 && accounts.length > 0) {
      let userListViewDetails = [];
      let userListViewId = 1;
      for (let user of users) {
        if (statusFilter === 'active' && !user.userStatus) continue;
        if (statusFilter === 'blocked' && user.userStatus) continue;
        if (![ 'Broker', 'Broker-user'].includes(user.role)) {
          const clientAccount = accounts.find(account => account.clientID === user.clientID);
          if (clientAccount) {
            if (doNotPerformUserFiltration || (loggedInUserClientId === user.clientID && loggedInUserName !== user.userName)) {
              userListViewDetails.push({
                id: userListViewId++,
                userName: user.userName,
                account: clientAccount.clientName.toUpperCase(),
                userStatus: user.userStatus ? 'ACTIVE' : 'BLOCKED',
                role: user.role.toUpperCase(),
              });
            }
          }
        }
      }
      setUserListView(userListViewDetails);
    }
  };

  const handleInputChange = async event => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'userName') {
      setUserNameError('');
      if (value) {
        const exists = await checkUsernameExistence(value);
        if (exists) setUserNameError('Username already exists');
      }
    } else if (name === 'email') {
      setEmailError('');
      setIsEmailAvailable(true);
      if (value && !/\S+@\S+\.\S+/.test(value)) {
        setEmailError('Please enter a valid email address');
      } else if (value) {
        const emailExists = await checkEmailExistence(value);
        if (emailExists) {
          setEmailError('Email already exists');
          setIsEmailAvailable(false);
        }
      }
    } else if (name === 'password') {
      setPasswordError('');
      setIsCopiedToClipboard(false);
      if (value && !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(value)) {
        setPasswordError('Password must be at least 8 characters, including one uppercase, one lowercase, one digit, and one special character');
      }
    }
  };

  const checkUsernameExistence = async username => {
    try {
      const response = await axios.get(`${BACKEND_URL}/v1/user?username=${encodeURIComponent(username)}`);
      return response.data.length > 0;
    } catch (error) {
      setSnackbarMessage('Failed to verify username availability.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return false;
    }
  };

  const checkEmailExistence = async email => {
    try {
      const response = await axios.get(`${BACKEND_URL}/v1/user?emailID=${encodeURIComponent(email)}`);
      return response.data.some(user => user.emailId === email);
    } catch (error) {
      setSnackbarMessage('Failed to verify email availability.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return false;
    }
  };

  const handleAccountChange = event => {
    const selectedAccountId = event.target.value;
    const activeLoginCount = users.filter(user => user.clientID === selectedAccountId).length;
    if (activeLoginCount >= 2) {
      setAccountError(true);
    } else {
      setAccountError(false);
    }
    setFormData(prev => ({ ...prev, accountId: selectedAccountId }));
  };

  const handlePasswordVisibility = () => {
    setPasswordVisibility(!passwordVisibility);
  };

  const handlePasswordGenerator = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+{}[]|;:,.<>?';
    let newPassword = '';
    for (let i = 0; i < length; i++) {
      newPassword += charset[Math.floor(Math.random() * charset.length)];
    }
    setFormData(prev => ({ ...prev, password: newPassword }));
    setPasswordError('');
    setIsCopiedToClipboard(false);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(formData.password);
    setIsCopiedToClipboard(true);
    setSnackbarMessage('Password copied to clipboard!');
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.userName) errors.userName = 'User Name is required';
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Valid email is required';
    // if (!formData.role) errors.role = 'Role is required';
    if (!formData.accountId && !isOnboard) errors.accountId = 'Account ID is required';
    if (isAddUserSelected && !formData.password) errors.password = 'Password is required';
    return errors;
  };

  const handleAddUserSelection = event => {
    event.preventDefault();
    setAddUserSelected(true);
    setUpdateUserSelected(false);
    resetFields();
  };

  const handleUpdateUserSelection = event => {
    event.preventDefault();
    setAddUserSelected(false);
    setUpdateUserSelected(true);
    resetFields();
  };

  const handleUpdateUserSelectionChange = event => {
    setLoading(true);
    setTimeout(() => {
      const chosenUser = users.find(user => user.userName === event.target.value);
      if (chosenUser) {
        setFormData({
          userName: chosenUser.userName,
          password: '',
                    role: 'Employee',
          email: chosenUser.emailId,
          userStatus: chosenUser.userStatus ? 'Active' : 'Inactive',
          accountId: chosenUser.clientID,
        });
      }
      setLoading(false);
    }, 1000);
  };

  const resetFields = () => {
    setFormData({
      userName: '',
      password: '',
      role: '',
      email: '',
      userStatus: 'Active',
      accountId: isOnboard ? client : '100',
    });
    setUserNameError('');
    setPasswordError('');
    setEmailError('');
    setAccountError(false);
    setPasswordVisibility(false);
    setIsCopiedToClipboard(false);
    setIsEmailAvailable(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setSnackbarMessage(Object.values(errors)[0]);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    try {
      if (isAddUserSelected) {
        let fetchUrl = formData.role === 'Admin' ? 
          `${BACKEND_URL}/v1/adminusersbyid?clientID=${encodeURIComponent(formData.accountId)}` : 
          `${BACKEND_URL}/v1/usersbyid?clientID=${encodeURIComponent(formData.accountId)}`;
        const response = await axios.get(fetchUrl);
        const users = response.data;
        if (formData.role === 'Admin' && users.length >= 1) {
          throw new Error('This client already has an Admin user. Cannot add more.');
        }
        if (formData.role === 'User' && users.length >= 2) {
          throw new Error('This client already has 2 users. Cannot add more.');
        }
        if (formData.role === 'Employee' && users.length >= 2) {
          throw new Error('This client already has 2 employees. Cannot add more.');
        }

        const createUserObject = {
          userName: formData.userName,
          password: formData.password,
                    role:"Employee",
          emailId: formData.email,
          clientID: formData.accountId,
          userStatus: formData.userStatus === 'Active',
          address: null,
        };

        const responseCreate = await axios.post(`${BACKEND_URL}/v1/user`, createUserObject);
        if (responseCreate.status === 200 || responseCreate.status === 201) {
          setIsUserCreated(true);
          setSnackbarMessage('Employee login created successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          resetFields();
          await Promise.all([getUsers(), generateListViewDetails()]);
          if (isOnboard) setTimeout(() => navigate('/profile'), 2000);
        }
      } else {
        if (loggedInUserName === formData.userName && formData.userStatus === 'Inactive') {
          throw new Error("Can't change inactive by self account");
        }
        if (['User', 'Employee'].includes(loggedInRole) && formData.userStatus === 'Inactive') {
          throw new Error(`${loggedInRole} cannot inactive other Users, Employees, or Admin`);
        }

        const updateUserObject = {
          userName: formData.userName,
          role: formData.role,
          emailId: formData.email,
          accountId: formData.accountId,
          userStatus: formData.userStatus === 'Active',
          name: formData.userName,
          phoneNo: '',
        };

        const response = await axios.patch(`${BACKEND_URL}/v1/user`, updateUserObject);
        if (response.status === 200 || response.status === 201) {
          setIsUserUpdated(true);
          setSnackbarMessage('Employee login updated successfully!');
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          resetFields();
          await Promise.all([getUsers(), generateListViewDetails()]);
        }
      }
    } catch (error) {
      setSnackbarMessage(error.message || 'Failed to perform operation.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (username, role) => {
    if (username === loggedInUserName) {
      setSnackbarMessage('You cannot delete yourself.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (role.toLowerCase() === 'admin') {
      setSnackbarMessage('Admin cannot be deleted, but you can change their status to inactive.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const confirmation = window.confirm(`Are you sure you want to delete the user: ${username}?`);
    if (confirmation) {
      try {
        await axios.post(`${BACKEND_URL}/v1/send-approval-email?username=${encodeURIComponent(username)}`);
        setSnackbarMessage('Approval email sent. Please check your inbox for further instructions.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } catch (error) {
        setSnackbarMessage(error.response?.data?.message || 'Failed to send approval email.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setIsUserCreated(false);
    setIsUserUpdated(false);
    setSnackbarMessage('');
    setSnackbarSeverity('success');
  };

  const userListViewColumns = [
    {
      field: 'userName',
      headerName: 'USER NAME',
      width: 280,
      align: 'left',
      headerAlign: 'left',
      flex: 1,
    },
    {
      field: 'account',
            headerName: 'ACCOUNT',
      width: 280,
      align: 'left',
      headerAlign: 'left',
      flex: 1,
    },
    {
      field: 'userStatus',
      headerName: 'STATUS',
      width: 250,
      align: 'left',
      headerAlign: 'left',
      flex: 1,
    },
    {
      field: 'role',
      headerName: 'ROLE',
      width: 220,
      align: 'left',
      headerAlign: 'left',
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 250,
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      renderCell: params =>
        params.row.role?.toLowerCase() !== 'admin' ? (
          <DeleteIcon
            sx={{ cursor: 'pointer', fontSize: 16, color: 'grey' }}
            onClick={() => handleDelete(params.row.userName, params.row.role)}
            color="error"
          />
        ) : null,
    },
  ];

  return (
    loggedIn && (
      <FormContainer maxWidth="xl">
        <FormPaper elevation={0}>
          <FormTitle variant="h6">
            {statusFilter === 'active' && 'Active Users'}
            {statusFilter === 'blocked' && 'Blocked Users'}
            {!statusFilter && 'Employee Management'}
          </FormTitle>

          <Box mb={4}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <SubmitButton
                  variant={isAddUserSelected ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={handleAddUserSelection}
                  sx={{
                    mr: 2,
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    minHeight: '30px',
                    lineHeight: '1.5',
                    backgroundColor: isAddUserSelected ? '#1976d2' : 'transparent',
                    color: isAddUserSelected ? '#ffffff' : '#1976d2',
                    '&:hover': {
                      backgroundColor: isAddUserSelected ? '#1565c0' : '#e3f2fd',
                      color: isAddUserSelected ? '#ffffff' : '#1976d2',
                    },
                  }}
                >
                  Add New User
                </SubmitButton>
              </Grid>
              {!isOnboard && (
                <Grid item>
                  <SubmitButton
                    variant={isUpdateUserSelected ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={handleUpdateUserSelection}
                    disabled={!hasEmployees}
                    sx={{
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      minHeight: '30px',
                      lineHeight: '1.5',
                      backgroundColor: isUpdateUserSelected ? '#1976d2' : 'transparent',
                      color: isUpdateUserSelected ? '#ffffff' : '#1976d2',
                      '&:hover': {
                        backgroundColor: isUpdateUserSelected ? '#1565c0' : '#e3f2fd',
                        color: isUpdateUserSelected ? '#ffffff' : '#1976d2',
                      },
                    }}
                  >
                    Update User 
                  </SubmitButton>
                </Grid>
              )}
            </Grid>
            {isUpdateUserSelected && !hasEmployees && (
              <Typography variant="body2" color="error" sx={{ mt: 2, textAlign: 'center' }}>
                No employees are available for updating.
              </Typography>
            )}
          </Box>

          {(isAddUserSelected || isUpdateUserSelected) && (
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {isUpdateUserSelected && (
                                    <Grid item xs={12} sm={4} sx={{ marginBottom: 1 }}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel sx={{ fontSize: '11px', top: '-30%' }}>User Name</InputLabel>
                      <Select
                        name="userName"
                        value={formData.userName}
                        onChange={handleUpdateUserSelectionChange}
                        label="User Name"
                        sx={{
                          '& .MuiInputBase-root': {
                            height: 24,
                            minHeight: 24,
                          },
                          '& .MuiInputBase-input': {
                            fontSize: 11,
                            padding: '2px',
                          },
                        }}
                          MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxHeight: '150px', // Adjust the maximum height of the dropdown
                                    minHeight: '150px', // Adjust the minimum height of the dropdown (optional)
                                    '& .MuiMenuItem-root': {
                                      fontSize: '12px', // Reduce font size inside the dropdown
                                      marginLeft: '5px', // Adjust left margin
                                      padding: '5px 15px', // Adjust the padding inside the items to control height
                                    },
                                  },
                                },
                              }}
                      >
                        {users
                          .filter(user => user.role === 'Employee' )
                          .map(user => (
                            <MenuItem key={user.userName} value={user.userName}>
                              {user.userName}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {isAddUserSelected && (
                  <Grid item xs={12} sm={4} sx={{ marginBottom: 2 }}>
                    <FormTextField
                      fullWidth
                      label="User Name"
                      name="userName"
                      value={formData.userName}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                      error={!!userNameError}
                      helperText={userNameError}
                      InputLabelProps={{
                        sx: { fontSize: '11px', top: '-10px' },
                      }}
                      sx={{
                        '& .MuiInputBase-root': { height: 25 },
                        '& .MuiInputBase-input': {
                          fontSize: 11,
                          padding: '4px',
                        },
                      }}
                    />
                  </Grid>
                )}
                {isAddUserSelected && (
                  <Grid item xs={12} sm={4} sx={{ marginBottom: 2 }}>
                    <FormTextField
                      fullWidth
                      label="Password"
                      name="password"
                      type={passwordVisibility ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      variant="outlined"
                      required
                      InputLabelProps={{
                        sx: { fontSize: '11px', top: '-10px' },
                      }}
                      sx={{
                        '& .MuiInputBase-root': { height: 25 },
                        '& .MuiInputBase-input': {
                          fontSize: 11,
                          padding: '4px',
                        },
                      }}
                      error={!!passwordError}
                      helperText={passwordError}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {passwordVisibility ? (
                              <VisibilityOffIcon onClick={handlePasswordVisibility} sx={{ fontSize: '20px' }} />
                            ) : (
                              <VisibilityIcon onClick={handlePasswordVisibility} sx={{ fontSize: '20px' }} />
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                    {formData.password.length === 0 ? (
                      <Typography
                        variant="body2"
                        onClick={handlePasswordGenerator}
                        sx={{ color: 'primary.main', cursor: 'pointer', mt: -2, mb: 2 }}
                      >
                        Generate Password
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        onClick={handleCopyToClipboard}
                        sx={{ color: 'primary.main', cursor: 'pointer', mt: -2, mb: 2 }}
                      >
                        {isCopiedToClipboard ? 'Copied to clipboard' : 'Copy to clipboard'}
                      </Typography>
                    )}
                  </Grid>
                )}
                <Grid item xs={12} sm={4} sx={{ marginBottom: 2 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel sx={{ fontSize: '11px', top: '-30%' }}>Role</InputLabel>
                    <Select
                      name="role"
                      value="Employee"
                      onChange={handleInputChange}
                      label="Role"
                      disabled={isUpdateUserSelected}
                      sx={{
                        '& .MuiInputBase-root': {
                          height: 22,
                          minHeight: 22,
                        },
                        '& .MuiInputBase-input': {
                          fontSize: 11,
                          padding: '2px',
                        },
                      }}
                      MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxHeight: '50px', // Adjust the maximum height of the dropdown
                                    minHeight: '50px', // Adjust the minimum height of the dropdown (optional)
                                    '& .MuiMenuItem-root': {
                                      fontSize: '12px', // Reduce font size inside the dropdown
                                      marginLeft: '5px', // Adjust left margin
                                      padding: '5px 15px', // Adjust the padding inside the items to control height
                                    },
                                  },
                                },
                              }}
                    >
                      {userRoles.map(userRole => (
                        <MenuItem key={userRole.id} value={userRole.value}>
                          {userRole.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ marginBottom: 2 }}>
                  <FormTextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    variant="outlined"
                    required
                    error={!!emailError}
                    helperText={emailError}
                    InputLabelProps={{
                      sx: { fontSize: '11px', top: '-10px' },
                    }}
                    sx={{
                      '& .MuiInputBase-root': { height: 28 },
                      '& .MuiInputBase-input': {
                        fontSize: 11,
                        padding: '4px',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} sx={{ marginBottom: 2 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Account Status</InputLabel>
                    <Select
                      name="userStatus"
                      value={formData.userStatus}
                      onChange={handleInputChange}
                      label="Account Status"
                      sx={{
                        '& .MuiInputBase-root': {
                          height: 22,
                          minHeight: 22,
                        },
                        '& .MuiInputBase-input': {
                          fontSize: 11,
                          padding: '2px',
                        },
                      }}
                        MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxHeight: '80px', // Adjust the maximum height of the dropdown
                                    minHeight: '80px', // Adjust the minimum height of the dropdown (optional)
                                    '& .MuiMenuItem-root': {
                                      fontSize: '12px', // Reduce font size inside the dropdown
                                      marginLeft: '5px', // Adjust left margin
                                      padding: '5px 15px', // Adjust the padding inside the items to control height
                                    },
                                  },
                                },
                              }}
                    >
                      {userStatuses.map(status => (
                        <MenuItem key={status.id} value={status.id}>
                          {status.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box mt={5} display="flex" justifyContent="center" gap={2}>
                <SubmitButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  sx={{
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    minHeight: '30px',
                    lineHeight: '1.5',
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={20} /> : isAddUserSelected ? (isOnboard ? 'Proceed' : 'Create') : 'Update'}
                </SubmitButton>
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/profile')}
                  sx={{
                    padding: '4px 8px',
                    fontSize: '0.75rem',
                    minHeight: '30px',
                    lineHeight: '1.5',
                  }}
                >
                  Cancel
                </SubmitButton>
              </Box>
            </Box>
          )}

          {!isOnboard && (
            <Box mt={4}>
              {loading || userListView === null ? (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress color="success" />
      </Box>
    ) : userListView.length > 0 ? (
                <StyledDataGrid
                  rows={userListView}
                  columns={userListViewColumns}
                  pageSize={5}
                  rowsPerPageOptions={[5]}
                  rowHeight={25}
                  columnHeaderHeight={31}
                  sx={{ width: '100%' }}
                  slots={{ toolbar: CustomToolbar }}
                  getRowClassName={params => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                />
              ) : loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                  <CircularProgress color="success" />
                </Box>
              ) : (
                <StyledDataGrid
                  rows={NoRecordsFoundRow}
                  columns={userListViewColumns}
                  columnHeaderHeight={36}
                  slots={{ toolbar: CustomToolbar }}
                  getRowClassName={params => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
                />
              )}
            </Box>
          )}

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              elevation={6}
              variant="filled"
              onClose={handleSnackbarClose}
              severity={snackbarSeverity}
              sx={{ width: '100%' }}
            >
              {(isUserCreated || isUserUpdated) && (
                <AlertTitle>
                  {isUserCreated ? 'Employee Created' : 'Employee Updated'}
                </AlertTitle>
              )}
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </FormPaper>
      </FormContainer>
    )
  );
};

export default ManageUsers;